import { useEffect, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { FaRegTrashAlt } from "react-icons/fa";
import { FormModal } from '../../layout/Modal/Modal';
import FertilizerCreateForm from './FertilizerCreateForm';
import { FertilizerTask } from "../../../interfaces/FertilizerTask";
import ParcelService from '../../../services/parcelService';
import IconButton from '../../common/IconButton';
import '../TableForm.css';
import { formatDateDisplay } from "../../../utils/date";

interface FormProps {
  parcelId: number
  cycleId: number
  cycleCompleted?: boolean
}

const FertilizerForm = (props: FormProps) => {
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const parcelService = new ParcelService();
  const [selectedTask, setSelectedTask] = useState<FertilizerTask | null>(null);

  const toggleModal = () => {
    setShowModal((prev) => !prev);
  };

  const toggleDetail = (task?: FertilizerTask) => {
    setSelectedTask(task || null);
    setShowDetail((prev) => !prev);
  };

  const [data, setData] = useState<FertilizerTask[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchFertilizerTasks = async () => {
    setLoading(true);
    try {
      const response = await parcelService.getFertalizerTasks(props.parcelId, props.cycleId);

      const processedData = response.map((task: FertilizerTask) => ({
        ...task,
        date: new Date(task.date), // Convert date string to Date object
      }));
      setData(processedData);
    } catch (error) {
      console.error("Error fetching fertilizer tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFertilizerTasks();
  }, [props.parcelId]);


  const updateRow = (updated: FertilizerTask) => {
    setData(prev => prev.map(e => e.id === updated.id ? updated : e));
    setShowDetail(false);
  };

  const handleDelete = (id: string) => {
    parcelService.deleteFertilizerTasks(props.parcelId, props.cycleId, Number(id))
    setData((prevData) => prevData.filter(entry => entry.id !== id));
  }

  const getTotalAmount = () => {
    return data.reduce((total, row) => total + (Number(row.quantityPerParcel) || 0), 0);
  }

  return (
    <div className="table-container px-1 md:px-5">
      <h1 className="text-xl font-bold mb-2 text-gray-900">Düngen</h1>
      {loading && (
        <div className="progress-container !h-2">
          <div className="progress-bar"></div>
        </div>
      )}

      <div className="table-wrapper hidden md:block">
        <table>
          <thead>
            <tr>
              <th>Art</th>
              <th>Menge/ha</th>
              <th>Datum</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 && <tr>
              <td colSpan={4} style={{ textAlign: "center" }}>Keine Einträge</td>
            </tr>}
            {data.map((row, index) => (
              <tr key={index} onClick={() => toggleDetail(row)}>
                <td>{row.kind}</td>
                <td>{row.quantityPerParcel}</td>
                <td>{formatDateDisplay(row.date!)}</td>
                <td>
                  <span className="icon-span cursor-pointer" onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(row.id)
                  }}>
                    <FaRegTrashAlt />
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className='py-2'>Total benötigter Dünger (pro/ha): <b>{getTotalAmount()}</b></p>
      </div>
      <div className='flex flex-1 flex-col w-full gap-3 md:hidden'>
        {data.map((row, index) =>
          <div key={index} className='flex flex-1 w-full gap-5 flex-col justify-between shadow-xl border-2 rounded-lg py-4 px-3 border-gray-300' onClick={() => toggleDetail(row)}>
            <div className='flex flex-1 flex-col gap-1'>
              <div className='flex flex-1 gap-2 items-center'>
                <h3 className='font-semibold text-base'>Art: </h3>
                <p className='text-base text-gray-600'>{row.kind}</p>
              </div>
              <div className='flex flex-1 gap-2 items-center'>
                <h3 className='font-semibold text-base'>Menge/ha: </h3>
                <p className='text-base text-gray-600'>{row.quantityPerParcel}</p>
              </div>
            </div>
            <div className='flex flex-1 justify-between'>
              <div className='flex flex-1 gap-2 items-center'>
                <h3 className='font-semibold text-base'>Datum: </h3>
                <p className="text-base text-gray-600">{formatDateDisplay(row.date!)}</p>
              </div>
              <button className="icon-span cursor-pointer" onClick={(e) => {
                e.stopPropagation();
                handleDelete(row.id)
              }}>
                <FaRegTrashAlt color="#ef4444" />
              </button>
            </div>
          </div>
        )}
      </div>

      <div className='flex flex-1 w-full flex-col md:hidden items-start pt-4 mb-8'>
        {data.length === 0 && <p className='text-center mb-0'>Keine Einträge</p>}
        <p className='py-2'>Total benötigter Dünger (pro/ha): <b>{getTotalAmount()}</b></p>
      </div>

      <IconButton className="add-btn"
        icon={<FiPlus size={26} />}
        onClick={() => toggleModal()}
      />

      <FormModal open={showModal} onClose={toggleModal} size="xl">
        <FertilizerCreateForm parcelId={props.parcelId} cycleId={props.cycleId} onSave={async () => { await fetchFertilizerTasks(); toggleModal(); }} isNew={true}></FertilizerCreateForm>
      </FormModal>
      <FormModal open={showDetail} onClose={toggleDetail} size="xl">
        <FertilizerCreateForm parcelId={props.parcelId} cycleId={props.cycleId} onSave={updateRow} isNew={false} task={selectedTask!} readOnly={props.cycleCompleted}></FertilizerCreateForm>
      </FormModal>
    </div>
  );
}

export default FertilizerForm;
